import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ArrowRightOnRectangleIcon } from "@heroicons/react/24/outline";
import { FaBars, FaBed, FaBell, FaExpand, FaTimes, FaUser } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import GlobalSearchSmart from "../common/FormInputs/GlobalSearchSmart";
import { getScreenNameForPath } from "../../utils/recentActivity";

const NAV_BRAND_SRC = `${process.env.PUBLIC_URL || ""}/nav-brand.svg`;

export default function CustomAppBar({
  handleDrawerToggle,
  handleOpenDialog,
  isLoggedIn,
  mobileOpen,
  onAreaClick,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const loginInfo = JSON.parse(localStorage.getItem("user") || "{}");
  const { control } = useForm();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  };

  const screenName = getScreenNameForPath(location.pathname);

  return (
    <div
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isLoggedIn || isScrolled ? "bg-[#F4F9FD]" : "bg-transparent"
      }`}
      role="banner"
    >
      <div
        className="relative flex justify-between items-center gap-2 px-2 py-2 sm:px-4 lg:px-8"
        onClick={onAreaClick}
      >
        <div className="flex items-center gap-2 min-w-0 shrink-0 z-10">
          <IconButton
            onClick={handleDrawerToggle}
            className="block lg:hidden text-gray-700 shrink-0"
            sx={{ display: { xs: "block", lg: "none" } }}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <FaTimes /> : <FaBars />}
          </IconButton>
          <img
            className="object-contain h-9 sm:h-10 lg:h-12 shrink-0"
            src={NAV_BRAND_SRC}
            alt="Shushrut HMIS"
          />
          {isFullscreen && screenName && (
            <span className="hidden sm:block text-sm md:text-base font-semibold text-[#563621] truncate max-w-xs ml-3">
              {screenName}
            </span>
          )}
        </div>

        <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md lg:max-w-[28rem] px-4 z-20 pointer-events-none">
          <div className="w-full pointer-events-auto">
            <GlobalSearchSmart
              control={control}
              name="globalSearch"
              placeholder="Search..."
              className="w-full"
            />
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-4 shrink-0 z-10 ml-auto">
          <div
            className="hidden md:flex text-gray-700 border rounded-xl bg-white p-2 hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 cursor-pointer"
            onClick={() => navigate("/bed-view")}
            role="button"
            tabIndex={0}
            aria-label="Bed view"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate("/bed-view");
              }
            }}
          >
            <FaBed />
          </div>
          <div
            className="hidden md:flex text-gray-700 border rounded-xl bg-white p-2 hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 cursor-pointer"
            onClick={toggleFullscreen}
            role="button"
            tabIndex={0}
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleFullscreen();
              }
            }}
          >
            <FaExpand />
          </div>
          <div className="hidden lg:flex text-gray-700 border rounded-xl bg-white p-2 hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 cursor-pointer">
            <FaBell />
          </div>
          <div
            className="flex items-center gap-1.5 sm:gap-2 cursor-pointer border rounded-xl bg-white py-1.5 px-2 sm:py-2 sm:px-4 hover:bg-blue-50 transition-all duration-300 hover:border-blue-400"
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <FaUser className="text-gray-700" />
            <p className="hidden sm:block text-sm text-[#563621] truncate max-w-[10rem]">
              {loginInfo?.loginName || loginInfo?.userName || "User"}
            </p>
          </div>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <div className="p-4 w-48 bg-white rounded-md shadow-lg">
              <div
                className="flex items-center justify-center gap-2 text-sm text-[#563621] cursor-pointer hover:text-red-600"
                onClick={() => {
                  setAnchorEl(null);
                  handleOpenDialog();
                }}
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                <span>Logout</span>
              </div>
            </div>
          </Popover>
        </div>
      </div>
    </div>
  );
}
